import React, { useEffect, useState } from 'react';
import { array, func } from 'prop-types';

import clsx from 'clsx';

import TreeMenu from 'react-simple-tree-menu';
import usePermissions from '../../hooks/usePermissions';

import classes from './TreeMenuWrapper.module.css';

const DEFAULT_PADDING = 16;
const ICON_SIZE = 8;
const LEVEL_SPACE = 16;

const toggleAction = props => {
    props.onToggleItem(props);

    if (props.toggleNode) {
        props.toggleNode();
    }
};

const handleSubscription = props => {
    props.setSubscription(props);
    props.onToggleSubscriptionModal(true);
};

const ToggleIcon = ({ on, onClick }) => (
    <span
        role="none"
        onClick={onClick}
        className={classes.toggleIcon}
    >
        {on ? <i className="fas fa-caret-down" /> : <i className="fas fa-caret-right" />}
    </span>
);

const FlavorIcon = ({type, style}) => {
    switch (type) {
        case 'campus':
            return (
                <img
                    src="/assets/images/campusTreeIcon.svg"
                    alt="campus icon"
                    style={style}
                    className={classes.flavorIcon}
                />
            );
        case 'company':
            return (
                <img
                    src="/assets/images/companyTreeIcon.svg"
                    alt="company icon"
                    style={style}
                    className={classes.flavorIcon}
                />
            );
        case 'client':
            return (
                <img
                    src="/assets/images/clientTreeIcon.svg"
                    alt="client icon"
                    style={style}
                    className={classes.flavorIcon}
                />
            );
        case 'site':
            return (
                <img
                    src="/assets/images/siteTreeIcon.svg"
                    alt="site icon"
                    style={style}
                    className={classes.flavorIcon}
                />
            );
        case 'area':
        case 'areaItem':
            return (
                <img
                    src="/assets/images/areaTreeIcon.svg"
                    alt="area icon"
                    style={style}
                    className={classes.flavorIcon}
                />
            );
        case 'equipment':
        case 'equipmentItem':
            return (
                <img
                    src="/assets/images/equipmentTreeIcon.svg"
                    alt="equipment icon"
                    style={style}
                    className={classes.flavorIcon}
                />
            );
        case 'system':
        case 'systemItem':
            return (
                <img
                    src="/assets/images/systemTreeIcon.svg"
                    alt="system icon"
                    style={style}
                    className={classes.flavorIcon}
                />
            );
        default:
            return null;
    }
};

const HealthIcon = ({health}) => {
    switch (health) {
        case 'high':
            return (
                <span className={clsx(classes.healthIcon, classes.high)} />
            );
        case 'medium':
            return (
                <span className={clsx(classes.healthIcon, classes.medium)} />
            );
        case 'low':
            return (
                <span className={clsx(classes.healthIcon, classes.low)} />
            );
        default:
            return (
                <span className={clsx(classes.healthIcon, classes.low)} />
            );
    }
};

const SubscribeIcon = ({onClick, type}) => {
    switch (type) {
        case 'area':
        case 'equipment':
        case 'system':
            return null;
        default:
            return (
                <img
                    src="/assets/images/subscribeIconOutline.svg"
                    alt="subscription icon"
                    className={classes.subscriptionIcon}
                    role="none"
                    onClick={onClick}
                />
            );
    }
};

const ServiceRequestIcon = ({onClick, type}) => {
    switch (type) {
        case 'area':
        case 'equipment':
        case 'system':
            return null;
        default:
            return (
                <img
                    src="/assets/images/ServiceManualIconFlat.svg"
                    alt="subscription icon"
                    className={classes.subscriptionIcon}
                    role="none"
                    onClick={onClick}
                />
            );
    }
};

const handleSvcRequest = (props) => {
    props.prepareServiceCallNode(props);
    props.onToggleCreateServiceRequestModal(true);
};

const ListItem = (props) => {
    return (
        <li
            role="none"
            style={{
                paddingLeft:    DEFAULT_PADDING + ICON_SIZE + props.level * LEVEL_SPACE,
                cursor:         'pointer',
                display:        'flex',
                justifyContent: 'space-between',
                alignItems:     'center'
            }}
            onClick={() => props.setActiveNode(props)}
            className={props.id === props.activeNode.id ? classes.active : ''}
        >
            <div>
                {props.hasNodes &&
                <React.Fragment>
                    <ToggleIcon
                        on={props.isOpen}
                        onClick={() => { toggleAction(props); }}
                    />
                    <FlavorIcon type={props.type} />
                </React.Fragment>
                }
                {!props.hasNodes &&
                <FlavorIcon
                    type={props.type}
                    style={{
                        marginLeft: '1.3rem'
                    }}
                />}
                <span role="none" onClick={() => { props.onClickItem(props); }}>{props.label}</span>
            </div>
            <div>
                <HealthIcon
                    health={props.faultScore}
                    type={props.type}
                />
                <SubscribeIcon
                    onClick={() => { handleSubscription(props); }}
                    type={props.type}
                />
                {
                    usePermissions('serviceManagementFull') &&
                    process.env.serviceCallEnabled &&
                    <ServiceRequestIcon
                        onClick={() => { handleSvcRequest(props); }}
                        type={props.type}
                    />
                }
            </div>
        </li>
    );
};

const TreeMenuWrapperList = ({
    items,
    onClickItem,
    onToggleCreateServiceRequestModal,
    onToggleSubscriptionModal,
    onToggleItem,
    prepareServiceCallNode,
    reset,
    saveTreeReset,
    setProgressBarLoading,
    setSubscription,
    ...props
}) => {
    const [activeNode, setActiveNode] = useState({});

    useEffect(() => {
        saveTreeReset(reset);
    }, []);

    return (
        <ul>
            {
                items.map(props => (
                    <ListItem
                        onToggleItem={onToggleItem}
                        onClickItem={onClickItem}
                        onToggleSubscriptionModal={onToggleSubscriptionModal}
                        onToggleCreateServiceRequestModal={onToggleCreateServiceRequestModal}
                        setSubscription={setSubscription}
                        prepareServiceCallNode={prepareServiceCallNode}
                        setActiveNode={setActiveNode}
                        activeNode={activeNode}
                        {...props}
                    />
                ))
            }
        </ul>
    );
};

const TreeMenuWrapper = ({ data, ...props }) => {
    if (!data.length) {
        return null;
    }

    return (
        <TreeMenu
            hasSearch={false}
            data={data}
            initialOpenNodes={[data[0].id]}
        >
            { ({ items, reset }) => (
                <TreeMenuWrapperList
                    items={items}
                    reset={reset}
                    {...props}
                />
            )}
        </TreeMenu>
    );
};

TreeMenuWrapper.defaultProps = {
    data:         [],
    onClickItem:  () => {},
    onToggleItem: () => {}
};

TreeMenuWrapper.propTypes = {
    data:         array.isRequired,
    onClickItem:  func.isRequired,
    onToggleItem: func.isRequired
};

export default TreeMenuWrapper;
